import { isEmpty, isNil } from 'lodash';
import { ComponentType, useState } from 'react';
import ReactSelect, { GroupBase, InputActionMeta, MultiValue, SingleValue } from 'react-select';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { FilterTagOption } from '@shared/components/filter/types';
import { i18n } from '@shared/util/i18n';
import {
  Control,
  IndicatorsContainer,
  Input,
  Menu,
  MenuList,
  MultiValueRemove,
  Placeholder,
  ValueContainer,
} from './ReactSelectComponents';
import styles from '../styles.module.css';

declare module 'react-select/dist/declarations/src/Select' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    hideMultiValue?: boolean;
    hasServerMore?: boolean;
    onLoadMore?: () => void;
  }
}

type DefaultOption = FilterTagOption;

interface OpenViewProps<Option extends DefaultOption = DefaultOption> {
  componentId: string;
  options: Option[];
  selectedOptions?: Option[];
  onSearch?(keyword: string): void;
  onChange(newValue: Option[]): void;
  components?: any;
  hideSelectedOptions?: boolean;
  isMultiSelect?: boolean;
  hideMultiValueAbove?: number;
  onLoadMore?(): void;
  hasServerMore?: boolean;
}

function HiddenMultiValue() {
  return null;
}

export function OpenView<Option extends DefaultOption = DefaultOption>({
  componentId,
  options,
  selectedOptions = [],
  onSearch = () => {},
  onChange = () => {},
  components = {},
  hideSelectedOptions = false,
  isMultiSelect = true,
  hideMultiValueAbove,
  onLoadMore,
  hasServerMore = false,
}: OpenViewProps<Option>) {
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (val: string, opt: InputActionMeta) => {
    if (opt.action === 'input-change') {
      setInputValue(val);
      onSearch(val);
    }
  };

  const handleChange = (val: MultiValue<Option> | SingleValue<Option>) => {
    const arr = Array.isArray(val) ? [...val] : val ? [val as Option] : [];
    onChange(arr);
  };

  const shouldHideMultiValue = !isNil(hideMultiValueAbove) && options.length > hideMultiValueAbove;

  return (
    <ReactSelect<Option, true, GroupBase<Option>>
      options={options}
      defaultValue={selectedOptions}
      autoFocus
      isMulti={isMultiSelect ? true : undefined}
      isClearable={false}
      menuIsOpen
      hideSelectedOptions={hideSelectedOptions}
      closeMenuOnSelect={false}
      closeMenuOnScroll={false}
      key={`select-${componentId}`}
      name={`select-${componentId}`}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      {...(shouldHideMultiValue ? { hideMultiValue: true } : {})}
      {...(hasServerMore || onLoadMore ? { hasServerMore, onLoadMore } : {})}
      // options https://react-select.com/components
      components={{
        Menu,
        MenuList,
        MultiValueRemove,
        Control,
        IndicatorsContainer,
        Placeholder,
        Input,
        ValueContainer,
        ...components,
        ...(shouldHideMultiValue ? { MultiValue: HiddenMultiValue } : {}),
      }}
      unstyled
      styles={{
        // because `unstyled` doesn't do everything
        input: () => ({}),
      }}
      classNames={{
        input: () => styles.searchInput,
      }}
      minMenuHeight={150}
      maxMenuHeight={Math.max(window.innerHeight * 0.2, 300)}
    />
  );
}

export function ClosedView<Option extends DefaultOption = DefaultOption>({
  selections,
  ItemComponent,
  maxWidth,
}: {
  selections: Option[];
  ItemComponent: ComponentType<{ selection: Option }>;
  maxWidth?: number;
}) {
  if (isEmpty(selections)) {
    return (
      <Text size="body1" inactive>
        {i18n('common.selectOptions')}
      </Text>
    );
  }

  return (
    <View row className={styles.closedView} vars={{ maxWidth }}>
      {ItemComponent &&
        selections?.map(selection => (
          <ItemComponent key={selection.value as string | number} selection={selection} />
        ))}
      {!ItemComponent && selections?.map(selection => selection.label).join(', ')}
    </View>
  );
}
